import React from 'react';

export function LoadingLogo({ size = 40, showText = false }) {
  const orbitSize = size * 1.8;
  const glowSize = size * 2.2;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '24px',
      gap: '16px',
    }}>
      {/* Main container with all layers */}
      <div style={{
        position: 'relative',
        width: orbitSize,
        height: orbitSize,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        {/* Outer glow pulse */}
        <div
          style={{
            position: 'absolute',
            width: glowSize,
            height: glowSize,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.06) 0%, transparent 70%)',
            animation: 'glowPulse 2.5s ease-in-out infinite',
          }}
        />

        {/* Expanding ring pulse */}
        <div
          style={{
            position: 'absolute',
            width: size,
            height: size,
            borderRadius: '50%',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            animation: 'ringExpand 2.5s ease-out infinite',
          }}
        />

        {/* Second expanding ring (offset timing) */}
        <div
          style={{
            position: 'absolute',
            width: size,
            height: size,
            borderRadius: '50%',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            animation: 'ringExpand 2.5s ease-out infinite 1.25s',
          }}
        />

        {/* Orbital track (subtle) */}
        <div
          style={{
            position: 'absolute',
            width: orbitSize * 0.85,
            height: orbitSize * 0.85,
            borderRadius: '50%',
            border: '1px solid rgba(255, 255, 255, 0.04)',
          }}
        />

        {/* Rotating orbital ring with gradient */}
        <svg
          style={{
            position: 'absolute',
            width: orbitSize * 0.85,
            height: orbitSize * 0.85,
            animation: 'orbitSpin 3s linear infinite',
          }}
          viewBox="0 0 100 100"
        >
          <defs>
            <linearGradient id="orbitGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
              <stop offset="50%" stopColor="#ffffff" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </linearGradient>
          </defs>
          <circle
            cx="50"
            cy="50"
            r="48"
            fill="none"
            stroke="url(#orbitGradient)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray="75 226"
          />
        </svg>

        {/* Counter-rotating inner arc */}
        <svg
          style={{
            position: 'absolute',
            width: orbitSize * 0.6,
            height: orbitSize * 0.6,
            animation: 'orbitSpinReverse 4s linear infinite',
            opacity: 0.4,
          }}
          viewBox="0 0 100 100"
        >
          <circle
            cx="50"
            cy="50"
            r="48"
            fill="none"
            stroke="rgba(255, 255, 255, 0.3)"
            strokeWidth="1"
            strokeLinecap="round"
            strokeDasharray="30 270"
          />
        </svg>

        {/* Orbiting dot */}
        <div
          style={{
            position: 'absolute',
            width: orbitSize * 0.85,
            height: orbitSize * 0.85,
            animation: 'orbitSpin 3s linear infinite',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '0',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.9)',
              boxShadow: '0 0 8px rgba(255, 255, 255, 0.6), 0 0 16px rgba(255, 255, 255, 0.3)',
            }}
          />
        </div>

        {/* Logo container with breathing effect */}
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            animation: 'logoBreathe 2.5s ease-in-out infinite',
          }}
        >
          {/* Soft glow behind logo */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: size * 1.2,
              height: size * 1.2,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
              filter: 'blur(8px)',
              animation: 'innerGlow 2.5s ease-in-out infinite',
            }}
          />

          {/* The logo */}
          <img
            src="/logo_hq_v2.png"
            alt="Loading..."
            style={{
              position: 'relative',
              width: size,
              height: size,
              filter: 'drop-shadow(0 0 12px rgba(255, 255, 255, 0.2))',
            }}
          />
        </div>
      </div>

      {/* Optional loading text */}
      {showText && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span style={{
            fontSize: '12px',
            color: 'var(--text-tertiary)',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            fontFamily: 'var(--font-mono)',
          }}>
            Loading
          </span>
          <span style={{
            display: 'flex',
            gap: '3px',
          }}>
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                style={{
                  width: '3px',
                  height: '3px',
                  borderRadius: '50%',
                  background: 'var(--text-tertiary)',
                  animation: `dotPulse 1.2s ease-in-out infinite ${i * 0.2}s`,
                }}
              />
            ))}
          </span>
        </div>
      )}

      <style>{`
        @keyframes logoBreathe {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(0.95);
            opacity: 0.85;
          }
        }

        @keyframes glowPulse {
          0%, 100% {
            transform: scale(0.9);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.1);
            opacity: 1;
          }
        }

        @keyframes innerGlow {
          0%, 100% {
            opacity: 0.8;
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1.15);
          }
        }

        @keyframes ringExpand {
          0% {
            transform: scale(1);
            opacity: 0.5;
          }
          100% {
            transform: scale(2.2);
            opacity: 0;
          }
        }

        @keyframes orbitSpin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes orbitSpinReverse {
          from {
            transform: rotate(360deg);
          }
          to {
            transform: rotate(0deg);
          }
        }

        @keyframes dotPulse {
          0%, 80%, 100% {
            opacity: 0.3;
            transform: scale(0.8);
          }
          40% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}
